// src/components/admin/components/VideoApprovalCard.jsx
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Video, Clock } from 'lucide-react';

export function VideoApprovalCard({
  video,
  approving,
  onApprove,
  onReject
}) {
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const handleReject = () => {
    onReject(video.key, rejectReason);
    setShowRejectForm(false);
    setRejectReason('');
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-[#243137] border-2 border-[#bd9f67]/20 rounded-lg p-6 hover:shadow-[0_0_25px_rgba(189,159,103,0.18)] hover:border-[#bd9f67]/40 transition"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="bg-[#bd9f67]/15 p-3 rounded-lg flex-shrink-0">
            <Video size={24} className="text-[#bd9f67]" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-[#f9f9f9] truncate">{video.name}</h3>
            <p className="text-sm text-[#a6a6b5]">By {video.artistName}</p>
            <div className="flex items-center gap-2 mt-2 text-xs text-[#7d7d8a]">
              <Clock size={14} />
              {new Date(video.uploadedAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              })}
            </div>
          </div>
        </div>
        <span className="bg-yellow-500/15 text-yellow-200 px-3 py-1 rounded-full text-xs font-semibold flex-shrink-0 ml-2">
          Pending
        </span>
      </div>

      {video.youtubeUrl && (
        <div className="bg-[#1e2a2f] p-3 rounded mb-4">
          <p className="text-xs text-[#a6a6b5] mb-2">YouTube URL:</p>
          <a
            href={video.youtubeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 hover:underline text-sm break-all"
          >
            Watch on YouTube ↗
          </a>
        </div>
      )}

      {video.description && (
        <div className="bg-[#1e2a2f] p-3 rounded mb-4 text-sm">
          <p className="text-[#c9c9d4]">{video.description}</p>
        </div>
      )}

      {!showRejectForm ? (
        <div className="flex gap-3">
          <button
            onClick={() => onApprove(video.key)}
            disabled={approving[video.key]}
            className="flex-1 flex items-center justify-center gap-2 bg-green-500/15 hover:bg-green-500/25 text-green-200 py-2 rounded-lg font-medium transition disabled:opacity-50"
          >
            <Check size={18} />
            Approve
          </button>
          <button
            onClick={() => setShowRejectForm(true)}
            disabled={approving[video.key]}
            className="flex-1 flex items-center justify-center gap-2 bg-red-500/15 hover:bg-red-500/25 text-red-200 py-2 rounded-lg font-medium transition disabled:opacity-50"
          >
            <X size={18} />
            Reject
          </button>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="space-y-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg"
        >
          <label className="block text-sm font-semibold text-red-200">
            Rejection Reason (Optional)
          </label>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="E.g., Inappropriate content, copyright concerns, poor quality..."
            className="w-full px-3 py-2 bg-transparent text-[#c9c9d4] border border-red-500/40 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
            rows="3"
          />
          <div className="flex gap-2">
            <button
              onClick={handleReject}
              disabled={approving[video.key]}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg font-medium transition disabled:opacity-50"
            >
              Confirm Reject
            </button>
            <button
              onClick={() => {
                setShowRejectForm(false);
                setRejectReason('');
              }}
              className="flex-1 bg-[#bd9f67]/15 hover:bg-[#bd9f67]/25 text-[#bd9f67] py-2 rounded-lg font-medium transition"
            >
              Cancel
            </button>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}